import {Camera, Images, Loader2, X} from "lucide-react";
import {useEffect, useRef, useState} from "react";
import {toast} from "sonner";

import {CameraPermissionDialog} from "@/src/modules/core/components/camera-permission-dialog";
import {ImagePickerAddTile} from "@/src/modules/core/components/image-picker-add-tile";
import {
  diagnoseCameraBlockReason,
  getCameraEnvironmentDiagnostics,
  getCameraPermissionDialogDescription,
  getCameraUnavailableDialogDescription,
  getGalleryPwaMemoryHint,
  queryCameraPermissionState,
  releaseMediaStream,
  requestCameraVideoStream,
} from "@/src/modules/core/lib/camera-permission";
import {compressImageFile} from "@/src/modules/core/lib/compress-image";
import {
  captureCameraCaptureFailedTelemetry,
  captureCameraCaptureSuccessTelemetry,
  captureCameraPermissionDeniedTelemetry,
  captureCameraUnavailableTelemetry,
  captureFilePickCancelledTelemetry,
  capturePhotoAttachedTelemetry,
} from "@/src/modules/core/lib/glitchtip/camera-telemetry";
import {
  captureImageCompressFailed,
  captureMediaUploadFailed,
  captureMediaUploadOffline,
} from "@/src/modules/core/lib/glitchtip/device-capture";
import {resolveFeatureFromWindowLocation} from "@/src/modules/core/lib/glitchtip/resolve-feature";
import {
  clearFilePickerPending,
  markFilePickerPending,
} from "@/src/modules/core/lib/file-picker-pending";
import {
  beginFilePickerSession,
  endFilePickerSession,
} from "@/src/modules/core/lib/file-picker-session";
import {useCreateFiles} from "@/src/modules/files/hooks";

import {Button} from "./button";
import CameraCapture from "./camera-capture";

const MAX_FILES_DEFAULT = 5;

function resolveImagePreviewUrl(image) {
  if (image instanceof File || image instanceof Blob) {
    return URL.createObjectURL(image);
  }
  if (typeof image === "string") {
    return image;
  }
  if (image && typeof image === "object") {
    return image.url ?? image.path ?? image.file_url ?? "";
  }
  return "";
}

export default function ImagePicker({
  onUpload,
  onDelete,
  onBeforePick,
  immediateUpload = false,
  images,
  errors,
  maxFiles = MAX_FILES_DEFAULT,
  captureFeature,
  captureOperation,
}) {
  const inputGalleryRef = useRef(null);
  const {mutateAsync: createFiles} = useCreateFiles();
  const [isMaxFilesExceeded, setIsMaxFilesExceeded] = useState(false);
  const [imagesUrls, setImagesUrls] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const cameraStreamRef = useRef(null);
  const [isCameraStarting, setIsCameraStarting] = useState(false);
  const [isCameraPermissionDialogOpen, setIsCameraPermissionDialogOpen] = useState(false);
  const [cameraPermissionDialogDescription, setCameraPermissionDialogDescription] = useState("");
  const [cameraDialogMode, setCameraDialogMode] = useState("permission");
  const isPickerOpenRef = useRef(false);
  const cameraSessionRef = useRef(0);
  const cameraRequestRef = useRef(null);
  const gestureSourceRef = useRef(null);

  const getTelemetryInput = () => ({
    feature: captureFeature ?? resolveFeatureFromWindowLocation(),
    operation: captureOperation,
  });

  useEffect(() => {
    return () => {
      cameraSessionRef.current += 1;
      cameraRequestRef.current = null;
      releaseMediaStream(cameraStreamRef.current);
      cameraStreamRef.current = null;
    };
  }, []);

  useEffect(() => {
    const finishPickerIfNeeded = () => {
      if (!isPickerOpenRef.current) {
        return;
      }
      window.setTimeout(() => {
        if (!isPickerOpenRef.current) {
          return;
        }
        isPickerOpenRef.current = false;
        endFilePickerSession();
      }, 300);
    };

    window.addEventListener("focus", finishPickerIfNeeded);
    return () => window.removeEventListener("focus", finishPickerIfNeeded);
  }, []);

  const openGalleryPicker = () => {
    void (async () => {
      await onBeforePick?.();
      isPickerOpenRef.current = true;
      beginFilePickerSession();
      markFilePickerPending("gallery", getTelemetryInput());
      inputGalleryRef.current?.click();
    })();
  };

  useEffect(() => {
    const urls = images.map((image) => resolveImagePreviewUrl(image));
    setImagesUrls(urls);

    return () => {
      images.forEach((image, index) => {
        const url = urls[index];
        if (
          (image instanceof File || image instanceof Blob) &&
          typeof url === "string" &&
          url.startsWith("blob:")
        ) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [images]);

  const processFiles = async (picked, {compress = true, photoSource} = {}) => {
    if (picked.length === 0) {
      return;
    }

    const existingUrls = images.filter((image) => typeof image === "string");
    const nextCount = existingUrls.length + picked.length;
    if (nextCount > maxFiles) {
      setIsMaxFilesExceeded(true);
      return;
    }

    setIsMaxFilesExceeded(false);

    const {feature, operation} = getTelemetryInput();

    let prepared = picked;
    if (compress) {
      try {
        prepared = await Promise.all(picked.map((file) => compressImageFile(file)));
      } catch {
        captureImageCompressFailed(feature, operation);
        return;
      }
    }

    if (photoSource) {
      for (const file of prepared) {
        capturePhotoAttachedTelemetry({
          feature,
          operation,
          photoSource,
          outputBytes: file.size,
          recompressed: compress,
        });
      }
    }

    if (!immediateUpload) {
      onUpload([...images, ...prepared]);
      return;
    }

    if (!navigator.onLine) {
      toast.error("Connect to the internet to upload images.");
      captureMediaUploadOffline(feature, operation);
      return;
    }

    setIsUploading(true);
    try {
      const uploadedUrls = await createFiles(prepared);
      onUpload([...existingUrls, ...uploadedUrls]);
    } catch (error) {
      captureMediaUploadFailed(feature, operation, error);
      onUpload([...images, ...prepared]);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadImages = (e) => {
    const wasPickerOpen = isPickerOpenRef.current;
    if (wasPickerOpen) {
      isPickerOpenRef.current = false;
      endFilePickerSession();
    }

    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";

    if (picked.length === 0) {
      clearFilePickerPending();
      if (wasPickerOpen) {
        captureFilePickCancelledTelemetry({
          ...getTelemetryInput(),
          pickerSource: "gallery",
        });
      }
      return;
    }

    clearFilePickerPending();
    void processFiles(picked, {compress: true, photoSource: "gallery"});
  };

  const stopCameraStream = () => {
    const current = cameraStreamRef.current;
    releaseMediaStream(current);
    cameraStreamRef.current = null;
    setCameraStream(null);
  };

  const assignCameraStream = (stream) => {
    cameraStreamRef.current = stream;
    setCameraStream(stream);
  };

  const endInAppCameraSession = () => {
    cameraSessionRef.current += 1;
    cameraRequestRef.current = null;
    setIsCameraOpen(false);
    stopCameraStream();
    endFilePickerSession();
  };

  const handleStreamFailure = (reason, details = {}) => {
    stopCameraStream();
    endFilePickerSession();
    const telemetry = getTelemetryInput();
    const environment = details.environment ?? getCameraEnvironmentDiagnostics();
    const gestureSource = gestureSourceRef.current ?? "click";

    if (reason === "permission_denied") {
      void (async () => {
        const permissionState = await queryCameraPermissionState();
        const diagnosis = diagnoseCameraBlockReason({
          permissionState,
          mediaErrorName: details.mediaErrorName,
          environment,
        });
        captureCameraPermissionDeniedTelemetry({
          ...telemetry,
          permissionState,
          mediaErrorName: details.mediaErrorName,
          environment,
          gestureSource,
          diagnosis,
        });
        setCameraDialogMode("permission");
        setCameraPermissionDialogDescription(
          getCameraPermissionDialogDescription({
            permissionState,
            mediaErrorName: details.mediaErrorName,
          }),
        );
        setIsCameraPermissionDialogOpen(true);
      })();
      return;
    }

    const diagnosis = diagnoseCameraBlockReason({
      permissionState: "unknown",
      mediaErrorName: details.mediaErrorName,
      environment,
    });
    captureCameraUnavailableTelemetry({
      ...telemetry,
      mediaErrorName: details.mediaErrorName,
      reason: details.reason,
      environment,
      gestureSource,
      diagnosis,
    });
    setCameraDialogMode("unavailable");
    setCameraPermissionDialogDescription(getCameraUnavailableDialogDescription(details));
    setIsCameraPermissionDialogOpen(true);
  };

  const beginCameraRequest = () => {
    if (isCameraStarting || isCameraOpen) {
      return null;
    }

    const request = requestCameraVideoStream();
    if (!request) {
      handleStreamFailure("unavailable", {
        reason: "get_user_media_unsupported",
        environment: getCameraEnvironmentDiagnostics(),
      });
      return null;
    }

    return request;
  };

  const fulfillCameraRequest = (request) => {
    const sessionId = cameraSessionRef.current;
    setIsCameraStarting(true);
    beginFilePickerSession();

    void request
      .then((stream) => {
        if (sessionId !== cameraSessionRef.current) {
          releaseMediaStream(stream);
          return;
        }
        onBeforePick?.();
        assignCameraStream(stream);
        setIsCameraOpen(true);
      })
      .catch((error) => {
        const name = error?.name ?? "UnknownError";
        if (name === "NotAllowedError" || name === "SecurityError") {
          handleStreamFailure("permission_denied", {mediaErrorName: name, environment});
        } else {
          handleStreamFailure("unavailable", {
            mediaErrorName: name,
            reason: "get_user_media_failed",
            environment,
          });
        }
      })
      .finally(() => {
        setIsCameraStarting(false);
        cameraRequestRef.current = null;
      });
  };

  /**
   * getUserMedia must run from pointerdown inside the sheet (not popover-portal-root).
   * Android drops user activation before click; without pointerdown the OS prompt may never show.
   */
  const handleCameraPointerDown = () => {
    if (cameraRequestRef.current || isCameraStarting || isCameraOpen) {
      return;
    }
    const sessionId = cameraSessionRef.current;
    const request = beginCameraRequest();
    if (!request) {
      return;
    }
    gestureSourceRef.current = "pointerdown";
    cameraRequestRef.current = request;

    void request.then((stream) => {
      if (sessionId !== cameraSessionRef.current) {
        releaseMediaStream(stream);
      }
    });
  };

  const openInAppCameraFromGesture = () => {
    const request = cameraRequestRef.current ?? beginCameraRequest();
    if (!request) {
      return;
    }
    if (!gestureSourceRef.current) {
      gestureSourceRef.current = "click";
    }
    fulfillCameraRequest(request);
  };

  const handleCameraClick = () => {
    openInAppCameraFromGesture();
  };

  const handleCameraPermissionRetryPointerDown = () => {
    if (cameraRequestRef.current || isCameraStarting || isCameraOpen) {
      return;
    }
    const request = beginCameraRequest();
    if (!request) {
      return;
    }
    gestureSourceRef.current = "permission_dialog_retry";
    cameraRequestRef.current = request;
  };

  const handleCameraPermissionRetry = () => {
    setIsCameraPermissionDialogOpen(false);
    openInAppCameraFromGesture();
  };

  const handleCameraCapture = (file, meta) => {
    if (meta) {
      captureCameraCaptureSuccessTelemetry({
        ...getTelemetryInput(),
        method: meta.method,
        sourceWidth: meta.sourceWidth,
        sourceHeight: meta.sourceHeight,
        outputBytes: file.size,
        flashEnabled: meta.flashEnabled,
        fillLightMode: meta.fillLightMode,
        torchProbed: meta.torchProbed,
        flashOnCaptureProbed: meta.flashOnCaptureProbed,
        flashOptimisticUi: meta.flashOptimisticUi,
      });
    }
    void processFiles([file], {compress: false});
  };

  const handleCameraCaptureFailed = () => {
    captureCameraCaptureFailedTelemetry(getTelemetryInput());
  };

  const galleryPwaMemoryHint = getGalleryPwaMemoryHint();

  const handleCameraStreamChange = (stream) => {
    stopCameraStream();
    assignCameraStream(stream);
  };

  const handleCameraStreamError = (reason, details) => {
    endInAppCameraSession();
    handleStreamFailure(reason, details);
  };

  const handleUseGallery = () => {
    setIsCameraPermissionDialogOpen(false);
    openGalleryPicker();
  };

  const handleCameraPermissionDismiss = () => {
    setIsCameraPermissionDialogOpen(false);
  };

  const removeImage = (index) => {
    setIsMaxFilesExceeded(false);
    onDelete(index);
  };

  const canAddMore = images.length < maxFiles;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-muted-foreground">
        {images.length} / {maxFiles} images
        {isUploading ? (
          <span className="ml-2 inline-flex items-center gap-1 text-muted-foreground">
            <Loader2 className="size-3 animate-spin" />
            Uploading…
          </span>
        ) : null}
      </p>
      <div className="mt-2 flex flex-wrap gap-4">
        {imagesUrls.map((url, index) => (
          <div key={url || `image-${index}`} className="relative">
            <img
              src={url || "/placeholder.svg"}
              alt={`Uploaded image ${index + 1}`}
              className="h-24 w-24 rounded-md border border-border object-cover"
            />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute -right-2 -top-2 size-6 rounded-full"
              onClick={() => removeImage(index)}
            >
              <X className="size-3" />
              <span className="sr-only">Remove image</span>
            </Button>
          </div>
        ))}

        {canAddMore ? (
          <>
            <ImagePickerAddTile
              label="Camera"
              aria-label="Take photo with camera"
              disabled={isCameraStarting}
              onPointerDown={handleCameraPointerDown}
              onClick={handleCameraClick}
              icon={
                isCameraStarting ? (
                  <Loader2 className="size-8 animate-spin text-muted-foreground" />
                ) : (
                  <Camera className="size-8 text-muted-foreground" />
                )
              }
            />
            <ImagePickerAddTile
              label="Gallery"
              aria-label="Choose from gallery"
              onClick={openGalleryPicker}
              icon={<Images className="size-8 text-muted-foreground" />}
            />
            <input
              ref={inputGalleryRef}
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleUploadImages}
              onCancel={() => {
                if (!isPickerOpenRef.current) {
                  return;
                }
                isPickerOpenRef.current = false;
                endFilePickerSession();
                clearFilePickerPending();
                captureFilePickCancelledTelemetry({
                  ...getTelemetryInput(),
                  pickerSource: "gallery",
                });
              }}
              multiple
            />
          </>
        ) : null}
      </div>
      {errors ? <p className="text-sm text-destructive">{errors.message}</p> : null}
      {isMaxFilesExceeded ? (
        <p className="text-sm text-destructive">You can only upload up to {maxFiles} images</p>
      ) : null}
      <CameraCapture
        open={isCameraOpen}
        stream={cameraStream}
        captureFeature={captureFeature}
        captureOperation={captureOperation}
        onCapture={handleCameraCapture}
        onCaptureFailed={handleCameraCaptureFailed}
        onClose={endInAppCameraSession}
        onStreamChange={handleCameraStreamChange}
        onStreamError={handleCameraStreamError}
      />
      <CameraPermissionDialog
        open={isCameraPermissionDialogOpen}
        title={cameraDialogMode === "unavailable" ? "Camera unavailable" : "Camera access needed"}
        description={cameraPermissionDialogDescription}
        galleryMemoryHint={galleryPwaMemoryHint}
        onRetry={handleCameraPermissionRetry}
        onRetryPointerDown={handleCameraPermissionRetryPointerDown}
        onUseGallery={handleUseGallery}
        onDismiss={handleCameraPermissionDismiss}
      />
    </div>
  );
}
